import { useState } from 'react'
import { Button } from '@/common/ui/button'
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import { useQueryClient } from '@tanstack/react-query'
import { GameQueryKeys } from '@/modules/game/keys'
import useMutationBatchUpdateProviderPreset from '@/modules/game/api/hooks/useMutationBatchUpdateProviderPreset'
import useQueryGetGameProviderList from '@/modules/game/api/hooks/useQueryGetGameProviderList'
import { Label } from '@/common/ui/label'
import { Input } from '@/common/ui/input'
import { Checkbox } from '@/common/ui/checkbox'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import useQueryGetPresetId from '@/modules/promotion/api/hooks/useQueryGetPresetId'
import { RtpPresetPrecedenceTooltip } from '@/common/components/RtpPresetPrecedenceTooltip'
import { isRtpSupportedProvider } from '@/modules/game/utils'
import { Settings2Icon } from 'lucide-react'

export default function BatchAssignRtpPreset() {
	const [isOpen, setIsOpen] = useState(false)
	const [presetValue, setPresetValue] = useState('')
	const [presetNumber, setPresetNumber] = useState('')
	const [clearPresetNumber, setClearPresetNumber] = useState(false)
	const { mutateAsync: batchUpdate, isPending } =
		useMutationBatchUpdateProviderPreset()
	const queryClient = useQueryClient()
	const { data: presets } = useQueryGetPresetId()
	const { data: allProviders } = useQueryGetGameProviderList({
		skip: 0,
		take: 500,
	})

	const providerIds = allProviders?.data.map((p) => p.id) || []

	const presetSelected = presetValue !== ''
	const numberEntered = presetNumber !== ''

	const canSubmit = presetSelected || clearPresetNumber || numberEntered

	const handleBatchUpdate = async () => {
		try {
			const payload: {
				providerIds: string[]
				defaultPreset?: string | null
				defaultPresetNumber?: number | null
			} = { providerIds }

			if (presetValue === '__clear__') {
				payload.defaultPreset = null
			} else if (presetSelected) {
				payload.defaultPreset = presetValue
			}

			if (clearPresetNumber) {
				payload.defaultPresetNumber = null
			} else if (numberEntered) {
				payload.defaultPresetNumber = Number(presetNumber)
			}

			const result = await batchUpdate(payload)
			queryClient.invalidateQueries({
				queryKey: [GameQueryKeys.GetGameProviderList],
			})

			if (result.skippedCount > 0) {
				toast.warning(
					`อัพเดทแล้ว ${result.updatedCount} รายการ ข้าม ${result.skippedCount} รายการ`,
				)
			} else {
				toast.success(`อัพเดทแล้ว ${result.updatedCount} รายการ`)
			}
			setIsOpen(false)
		} catch (error) {
			showErrorToast(error as Error)
		}
	}

	const handleClose = () => {
		setIsOpen(false)
		setPresetValue('')
		setPresetNumber('')
		setClearPresetNumber(false)
	}

	const providerCount =
		allProviders?.data.filter((p) => isRtpSupportedProvider(p.name)).length || 0

	return (
		<>
			<Button
				variant="outline"
				size="sm"
				onClick={() => setIsOpen(true)}
				className="gap-2 sm:h-9"
			>
				<Settings2Icon className="h-4 w-4" />
				เปลี่ยน preset สำหรับทุกค่าย
			</Button>

			<Dialog open={isOpen} onOpenChange={handleClose}>
				<DialogContent className="sm:max-w-[425px]">
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2">
							ตั้งค่า RTP Preset ทั้งหมด
							<RtpPresetPrecedenceTooltip highlightLevel={6} />
						</DialogTitle>
					</DialogHeader>
					<div className="grid gap-4 py-4">
						<p className="text-sm text-muted-foreground">
							กำลังจะอัพเดท RTP Preset สำหรับทั้ง {providerCount} ค่ายเกม
						</p>
						<div className="grid gap-2">
							<Label>เลือก RTP Preset</Label>
							<Select
								value={presetValue}
								onValueChange={setPresetValue}
								disabled={isPending}
							>
								<SelectTrigger>
									<SelectValue placeholder="ไม่เปลี่ยน" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="">ไม่เปลี่ยน</SelectItem>
									<SelectItem value="__clear__">ล้างค่า RTP Preset</SelectItem>
									{presets?.presets.map((preset) => (
										<SelectItem key={preset.id} value={preset.id}>
											{preset.note}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div className="grid gap-2">
							<Label>ตั้งค่าเปอร์เซ็น RTP (30-99)</Label>
							<Input
								type="number"
								min={30}
								max={99}
								placeholder="ไม่เปลี่ยน"
								value={presetNumber}
								onChange={(e) => setPresetNumber(e.target.value)}
								disabled={isPending || clearPresetNumber}
							/>
							<div className="flex items-center gap-2">
								<Checkbox
									id="clear-preset-number"
									checked={clearPresetNumber}
									onCheckedChange={(checked) => {
										setClearPresetNumber(!!checked)
										if (checked) setPresetNumber('')
									}}
									disabled={isPending}
								/>
								<Label
									htmlFor="clear-preset-number"
									className="cursor-pointer text-sm"
								>
									ล้างค่า RTP เปอร์เซ็น
								</Label>
							</div>
						</div>
					</div>
					<DialogFooter>
						<Button type="button" variant="outline" onClick={handleClose}>
							ยกเลิก
						</Button>
						<Button
							onClick={handleBatchUpdate}
							disabled={isPending || !canSubmit}
						>
							{isPending ? 'กำลังบันทึก...' : 'บันทึกทั้งหมด'}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	)
}
